import { getUserTimeZone } from 'shared/utils/common';
import yup from "shared/validation/yupExtensions";

// RequestByFilter

export interface ResourceAvailabilityByFilterRequest {
    tenantIds: string[] | null;
    employeeIds: string[] | null;
    from: string | null;
    to: string | null;
    timeZone: string | null;
}

export const initialResourceAvailabilityByFilterRequest: ResourceAvailabilityByFilterRequest = {
    tenantIds: null,
    employeeIds: null,
    from: null,
    to: null,
    timeZone: getUserTimeZone(),
};

export const ResourceAvailabilityByFilterSchema: yup.ObjectSchema<ResourceAvailabilityByFilterRequest> = yup.object({
    tenantIds: yup.array().of(yup.string().required())
        .nullable()
        .defined(),
    employeeIds: yup.array().of(yup.string().required())
        .nullable()
        .defined(),
    from: yup.string()
        .nullable()
        .defined(),
    to: yup.string()
        .nullable()
        .defined()
        .test(
            "to-after-from",
            "To date cannot be earlier than from date.",
            function (value) {
                const { from } = this.parent;
                if (!value || !from) return true;
                return new Date(value) >= new Date(from);
            }),
    timeZone: yup.string().nullable().defined(),
});

// GENERAL

export interface ResourceOverviewState {
    resourceAvailability: ResourceAvailability | null;
    filterData: ResourceAvailabilityFilterData | null;
    legend: ResourceAvailabilityLegend | null;
    activeFilter: ResourceAvailabilityByFilterRequest | null;
    isLoading: boolean;
    error?: string;
}

export const initialResourceOverviewState: ResourceOverviewState = {
    resourceAvailability: null,
    filterData: null,
    legend: null,
    activeFilter: initialResourceAvailabilityByFilterRequest,
    isLoading: false,
    error: undefined,
}

// ResourceAvailability

export interface ResourceAvailability {
    tenantEmployees: Record<string, TenantEmployeeResources>;
}

export interface TenantEmployeeResources {
    tenantName: string,
    employees: EmployeeResources[],
}

export interface EmployeeResources {
    employeeId: string;
    fullName: string;
    days: EmployeeAvailabilityDay[];
}

export interface EmployeeAvailabilityDay {
    date: string;
    state: ResourceAvailabilityDayState;
    holidayName: string | null;
    absenceTypeId: string | null;
    isApproved: boolean | null;
    description: string | null;
}

export enum ResourceAvailabilityDayState {
    WorkingDay = 1,
    NonWorkingDay = 2,
    Holiday = 3,
    Absence = 4
}

// Multi-Tenant
export interface MergedEmployeeTenant {
    tenantId: string;
    tenantName: string;
    days: EmployeeAvailabilityDay[];
}

export interface MergedEmployee {
    employeeId: string;
    fullName: string;
    tenants: MergedEmployeeTenant[];
}

// FILTER

export interface GetListResponse<T> {
    value: T;
    label: string;
    isActive: boolean;
}

export interface GetListDetailResponse<T> {
    value: T;
    label: string;
    detail: string;
}

export interface ResourceAvailabilityFilterData {
    tenants: GetListResponse<string>[];
    employees: Record<string, GetListDetailResponse<string>[]>;
}

// LEGEND

export interface ResourceAvailabilityLegend {
    items: AvailabilityLegendItem[];
}

export interface AvailabilityLegendItem {
    label: string;
    name: string;
    color: string | null;
}

